import React from 'react'
import { useTranslation } from 'react-i18next'
import type { AutoBattleResult } from './worldMapApi'
import { isPveAutoBattleAttackerWin } from './worldMapApi'
import { MvMDefeatModal } from '../../components/MvMBattle/MvMDefeatModal'
import { MvMVictoryModal } from '../../components/MvMBattle/MvMVictoryModal'
import { mvmResultSummaryTextStyle } from '../../components/MvMBattle/mvmBattleResultUi'

/** Match prior world-map stacking above map chrome. */
const AUTO_BATTLE_MODAL_Z = 10080

function buildLootRecord(loot: AutoBattleResult['lootWon']): Record<string, number> | null {
  if (!loot) return null
  const o: Record<string, number> = {}
  const w = (v?: number) => Math.max(0, Math.floor(Number(v) || 0))
  const put = (k: string, v: number) => {
    if (v > 0) o[k] = v
  }
  put('wood', w(loot.wood))
  put('stone', w(loot.stone))
  put('iron', w(loot.iron))
  return Object.keys(o).length ? o : null
}

function AutoBattleResultDescription({ type, won }: { type: 'barbarian' | 'defense_tower'; won: boolean }) {
  const { t } = useTranslation()
  const subtitle =
    type === 'barbarian'
      ? won
        ? t('worldMap.autoBattleBarbarianWon', { defaultValue: 'The barbarian village has been defeated!' })
        : t('worldMap.autoBattleBarbarianLost', { defaultValue: 'Your forces were repelled by the barbarians.' })
      : won
        ? t('worldMap.autoBattleTowerWon', { defaultValue: 'The defense tower is now under your control!' })
        : t('worldMap.autoBattleTowerLost', { defaultValue: 'The monster garrison held the tower.' })

  return (
    <div>
      <p style={{ ...mvmResultSummaryTextStyle, marginBottom: 0 }}>{subtitle}</p>
    </div>
  )
}

export function WorldMapAutoBattleResultModal({
  open,
  onClose,
  result,
  type,
}: {
  open: boolean
  onClose: () => void
  result: AutoBattleResult | null
  type: 'barbarian' | 'defense_tower' | null
}) {
  const { t } = useTranslation()
  if (!open || !result || !type) return null

  const won = isPveAutoBattleAttackerWin(result.battleOutcome)
  const lootRecord = buildLootRecord(result.lootWon)
  const showLoot = won && type === 'barbarian' && !!lootRecord
  const dismiss = t('mvm.victory.returnToMap')
  const desc = <AutoBattleResultDescription type={type} won={won} />

  /** Server auto-battle returns XP on `pveXp` (see `grantWorldMapPveExperience`); top-level `userXp` / `characterXp` are unused. */
  const pve = result.pveXp
  const userXp = Math.max(0, Math.floor(Number(pve?.userXp ?? result.userXp) || 0))
  const characterXp = Math.max(
    0,
    Math.floor(Number(pve?.characterXpEach ?? result.characterXp) || 0),
  )
  if (won) {
    return (
      <MvMVictoryModal
        overlayZIndex={AUTO_BATTLE_MODAL_Z}
        showResourceLoot={showLoot}
        seizedResources={lootRecord}
        serverSummary={null}
        descriptionContent={desc}
        showBattleRewards
        dismissButtonLabel={dismiss}
        characterXp={characterXp}
        userXp={userXp}
        onClose={onClose}
      />
    )
  }

  return (
    <MvMDefeatModal
      mode="standard"
      overlayZIndex={AUTO_BATTLE_MODAL_Z}
      seizedResources={null}
      serverSummary={null}
      headline={undefined}
      standardDetail={undefined}
      summaryOnly={false}
      descriptionContent={desc}
      showBattleRewards
      characterXp={characterXp}
      userXp={userXp}
      dismissButtonLabel={dismiss}
      onClose={onClose}
    />
  )
}
